import type { PropsWithChildren } from 'react'
import { createContext } from 'use-context-selector'
import { useCallback, useEffect, useState } from 'react'
import STORAGE_KEYS from '../../lib/storageKeys'
import { Storage } from '../../lib/storage'
import useNetworkStatus from '../../lib/hooks/useNetworkStatus'
import {
  CampDocument,
  NotificationDocument,
  SpecialTalentsDocument, TalentDocument, TalentLatestVoteDocument,
  useAddMultipleToGroupMutation,
  useRemoveMultipleFromGroupMutation,
  useShortVoteMutation,
  VotesDocument
} from '../../lib/apollo/types'

interface Vote { talentId: string, isTalent: boolean, values: number[], networkId?: string, class: number }

interface GroupOperation {
  type: 'add' | 'remove'
  groupId: string
  campId: string
  participantIds: string[]
}

export interface OfflineCacheType {
  addVote: (vote: Vote) => any
  votesToSync: number
  addGroupOperation: (operation: GroupOperation) => any
  groupOperationsToSync: number
}

export const OfflineCache = createContext<OfflineCacheType>({
  addVote: () => {},
  votesToSync: 0,
  addGroupOperation: () => {},
  groupOperationsToSync: 0
})

const OfflineCacheProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [votesToSync, setVotesToSync] = useState<Vote[]>([])
  const [groupOperationsToSync, setGroupOperationsToSync] = useState<GroupOperation[]>([])
  const networkStatus = useNetworkStatus()

  const [vote] = useShortVoteMutation()
  const [addToGroup] = useAddMultipleToGroupMutation()
  const [removeFromGroup] = useRemoveMultipleFromGroupMutation()

  useEffect(() => {
    // init state
    Storage.get({ key: STORAGE_KEYS.CACHE.VOTES }).then(({ value }) => {
      if (value) {
        setVotesToSync(JSON.parse(value))
      }
    })

    Storage.get({ key: STORAGE_KEYS.CACHE.GROUP_OPERATIONS }).then(({ value }) => {
      if (value) {
        setGroupOperationsToSync(JSON.parse(value))
      }
    })
  }, [])

  const addVote = useCallback(async (vote: any) => {
    await Storage.set({
      key: STORAGE_KEYS.CACHE.VOTES,
      value: JSON.stringify([...votesToSync, vote])
    })

    setVotesToSync(v => [...v, vote])
  }, [votesToSync, setVotesToSync])

  const addGroupOperation = useCallback(async (operation: GroupOperation) => {
    await Storage.set({
      key: STORAGE_KEYS.CACHE.GROUP_OPERATIONS,
      value: JSON.stringify([...groupOperationsToSync, operation])
    })

    setGroupOperationsToSync(ops => [...ops, operation])
  }, [groupOperationsToSync, setGroupOperationsToSync])

  useEffect(() => {
    if (votesToSync.length === 0) return
    if (networkStatus === 'none') return

    const currentVote = votesToSync[0]

    vote(
      {
        variables: {
          talentId: currentVote.talentId,
          input: {
            values: currentVote.values,
            networkId: currentVote.networkId ?? '',
            talent: currentVote.isTalent,
            class: currentVote.class ?? 1
          }
        },
        refetchQueries: [
          {
            query: TalentDocument,
            variables: { id: currentVote.talentId }
          },
          {
            query: VotesDocument,
            variables: { talentId: currentVote.talentId }
          },
          {
            query: SpecialTalentsDocument,
            variables: { campId: currentVote.networkId ?? '' }
          },
          {
            query: NotificationDocument
          },
          {
            query: TalentLatestVoteDocument,
            variables: {
              talentId: currentVote.talentId,
              campId: currentVote.networkId ?? ''
            }
          }
        ],
        onError: (e) => {
          console.error(e)
        },
        onCompleted: () => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [_, ...rest] = votesToSync
          Storage.set({
            key: STORAGE_KEYS.CACHE.VOTES,
            value: JSON.stringify(rest)
          })
          setVotesToSync(([_, ...rest]) => rest.filter(Boolean))
        }
      }
    )
  }, [networkStatus, vote, votesToSync])

  // Sync group operations when back online
  useEffect(() => {
    if (groupOperationsToSync.length === 0) return
    if (networkStatus === 'none') return

    const currentOperation = groupOperationsToSync[0]

    const mutation = currentOperation.type === 'add' ? addToGroup : removeFromGroup

    mutation({
      variables: {
        groupId: currentOperation.groupId,
        campId: currentOperation.campId,
        participantIds: currentOperation.participantIds
      },
      refetchQueries: [
        {
          query: CampDocument,
          variables: { campId: currentOperation.campId }
        }
      ],
      onError: (e) => {
        console.error('Group operation sync failed:', e)
      },
      onCompleted: () => {
        console.log(`Group operation synced: ${currentOperation.type}`)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [_, ...rest] = groupOperationsToSync
        Storage.set({
          key: STORAGE_KEYS.CACHE.GROUP_OPERATIONS,
          value: JSON.stringify(rest)
        })
        setGroupOperationsToSync(([_, ...rest]) => rest.filter(Boolean))
      }
    })
  }, [networkStatus, addToGroup, removeFromGroup, groupOperationsToSync])

  return (
        <OfflineCache.Provider value={{
          addVote,
          votesToSync: votesToSync.length,
          addGroupOperation,
          groupOperationsToSync: groupOperationsToSync.length
        }}>
            {children}
        </OfflineCache.Provider>
  )
}

export default OfflineCacheProvider
